import React, { useState } from "react"
import { AbsoluteFill, Html5Video, Img, OffthreadVideo, Sequence, getRemotionEnvironment, useCurrentFrame, useVideoConfig } from "remotion"
import { joinSplitPieces, msToFrames } from "../../utils/timing"
import { getKenBurnsTransform, type MotionPreset } from "../../utils/interpolation"
import type { Track } from "../../schemas"

interface BRollTrackProps {
  track: Track
}

export const BRollTrack: React.FC<BRollTrackProps> = ({ track }) => {
  const { fps } = useVideoConfig()
  const { isPlayer } = getRemotionEnvironment()

  const segments = joinSplitPieces(track.sections.flatMap(s => s.segments))

  return (
    <AbsoluteFill>
      {segments.map((segment) => {
        const inFrame = msToFrames(segment.timeline.in, fps)
        const duration = msToFrames(segment.timeline.out - segment.timeline.in, fps)

        return (
          <Sequence key={segment.id} from={inFrame} durationInFrames={Math.max(duration, 1)} premountFor={isPlayer ? fps : 0}>
            <BRollSegmentView
              segment={segment}
              durationInFrames={duration}
            />
          </Sequence>
        )
      })}
    </AbsoluteFill>
  )
}

const BRollSegmentView: React.FC<{
  segment: Record<string, any>
  durationInFrames: number
}> = ({ segment, durationInFrames }) => {
  const [videoFailed, setVideoFailed] = useState(false)
  const frame = useCurrentFrame()
  const { fps } = useVideoConfig()
  // The right half of a split B-roll video carries on from where the left half stopped.
  const trimFrames = msToFrames(segment.trimStartMs ?? 0, fps) || undefined
  const content = segment.content
  if (!content?.mediaInfo) return null

  const mediaInfo = content.mediaInfo
  const motion = (segment.motion ?? "zoom_in") as MotionPreset
  const { scale, translateX, translateY } = getKenBurnsTransform(motion, frame, durationInFrames)
  const { isPlayer } = getRemotionEnvironment()

  const mediaType = String(mediaInfo.mediaType ?? "").toLowerCase()
  const isVideo = mediaType === "video"
  const src = mediaInfo.originUrl || mediaInfo.previewUrl
  const fallbackImageSrc = mediaInfo.coverImage || mediaInfo.previewUrl
  if (!src && !fallbackImageSrc) return null

  const shouldRenderVideo = isVideo && !!src && !videoFailed

  return (
    <AbsoluteFill
      style={{
        overflow: "hidden",
      }}
    >
      <div
        style={{
          width: "100%",
          height: "100%",
          transform: `scale(${scale}) translate(${translateX}%, ${translateY}%)`,
        }}
      >
        {shouldRenderVideo ? (
          isPlayer ? (
            <Html5Video
              src={src}
              pauseWhenBuffering
              muted
              trimBefore={trimFrames}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={(err) => {
                setVideoFailed(true)
                console.warn(`[Remotion] B-roll video failed in Player preview, showing its cover image: ${err.message}`)
              }}
            />
          ) : (
            // No onError: Remotion's OffthreadVideo never clears its delayRender after calling
            // onError, so a fallback here turns a clear download error into a 28 s timeout.
            <OffthreadVideo
              src={src}
              trimBefore={trimFrames}
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          )
        ) : (
          <Img
            src={fallbackImageSrc || src}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        )}
      </div>
    </AbsoluteFill>
  )
}
